/**
 * `sessionGuard` — may this person be on a flow screen at all?
 *
 * Awaits the eligibility chain once, then enforces its answer: a resolved block is
 * written to `AccessBlockStore` and rendered in place by the shell; a resolved
 * business context lets the navigation through.
 *
 * ### It does not re-check anything
 *
 * The plan describes this guard as checking "authenticated, active OCN,
 * administrator". All three are already checked — by `EligibilityService`, in a
 * fixed order, alongside five others, arriving as exactly one `EligibilityBlock`.
 * Re-deriving any of them here would put the rules in two places, and Q-14 asks
 * precisely which service owns each rule and when it runs. A second implementation
 * on the client would be an answer to that question that nobody gave.
 *
 * So the guard has one job: honour the answer. Which of the fourteen triggers it is
 * enforcing is the chain's business, not the guard's.
 *
 * ### There is no separate `featureFlagGuard`
 *
 * The plan lists one. It would have nothing to do. The feature flag is step 1 of the
 * chain — before authentication, since if the flow is off nobody's account details
 * matter — and arrives as a `FEATURE_DISABLED` block like any other. The only
 * client-side thing a flag guard could read is `BusinessContext.featureEnabled`,
 * which the chain hardcodes to `true` on the success path, because a context only
 * exists when the flag is on. A guard reading it could never fire.
 *
 * Where the flag should be read — globally, per OCN, per segment, as a percentage
 * rollout — is Q-27, and the answer changes which layer owns it. One reading point,
 * marked, beats two that can disagree.
 *
 * ### A failed chain is not a block
 *
 * `phase === 'failed'` means the checks could not be run, which is a different thing
 * from a check saying no, and no source defines it (Q-14). S-01 already renders that
 * case honestly — "we cannot verify whether you can use this flow", with a retry —
 * so the guard sends the customer there rather than inventing a fifteenth trigger to
 * describe an outage with.
 */
import { inject } from '@angular/core';
import { RedirectCommand, Router } from '@angular/router';
import type { CanActivateFn } from '@angular/router';
import { AccessBlockStore } from '../access/access-block.store';
import { ROUTE_FOR } from '../flow/flow-steps';
import { SessionStore } from '../stores/session.store';

export const sessionGuard: CanActivateFn = async (_route, state) => {
  const session = inject(SessionStore);
  const access = inject(AccessBlockStore);
  const router = inject(Router);

  // Resolves at most once per app load; concurrent guards share the promise.
  await session.ensureLoaded();

  if (session.phase() === 'failed') {
    access.clear();
    return new RedirectCommand(router.parseUrl(ROUTE_FOR['S-01']));
  }

  const block = session.block();
  if (block !== null) {
    access.blockAt(state.url, block);
    // True, not false: the navigation has to complete for the URL, the breadcrumbs
    // and the page measure to be the ones the customer asked for. The shell renders
    // the block in the outlet's place, so the screen component is never created.
    return true;
  }

  access.clear();
  return true;
};
